import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, FolderOpen, List, X, AlertTriangle } from 'lucide-react'
import { getPersonalLists, createPersonalList, deletePersonalList, getListItems, removeListItem } from '../../services/api'
import type { PersonalList, PersonalItem } from '../../types'

export default function MyLists() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [selectedListId, setSelectedListId] = useState<string | null>(null)
  const [newListName, setNewListName] = useState('')
  const [newListDescription, setNewListDescription] = useState('')

  const { data: lists = [], isLoading: listsLoading } = useQuery({
    queryKey: ['personalLists'],
    queryFn: getPersonalLists,
  })

  const { data: listItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['listItems', selectedListId],
    queryFn: () => getListItems(selectedListId!),
    enabled: !!selectedListId,
  })

  const createMutation = useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) =>
      createPersonalList(name, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalLists'] })
      setShowCreateModal(false)
      setNewListName('')
      setNewListDescription('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (listId: string) => deletePersonalList(listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalLists'] })
      if (selectedListId === deleteConfirm) {
        setSelectedListId(null)
      }
      setDeleteConfirm(null)
    },
  })

  const removeItemMutation = useMutation({
    mutationFn: ({ listId, resourceId }: { listId: string; resourceId: string }) =>
      removeListItem(listId, resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', selectedListId] })
      queryClient.invalidateQueries({ queryKey: ['personalLists'] })
    },
  })

  const handleCreate = () => {
    if (!newListName.trim()) return
    createMutation.mutate({ name: newListName.trim(), description: newListDescription.trim() || undefined })
  }

  const selectedList = lists.find((l) => l.list_id === selectedListId)

  // List detail view
  if (selectedListId && selectedList) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button
          onClick={() => setSelectedListId(null)}
          className="text-acad-600 hover:text-acad-700 mb-4 flex items-center gap-1 text-sm font-medium"
        >
          &larr; Back to My Lists
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{selectedList.name}</h1>
          {selectedList.description && (
            <p className="text-gray-500 mt-1">{selectedList.description}</p>
          )}
          <p className="text-sm text-gray-400 mt-1">
            {selectedList.item_count} item{selectedList.item_count !== 1 ? 's' : ''} &middot;
            Created {new Date(selectedList.created_at).toLocaleDateString()}
          </p>
        </div>

        {itemsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/4" />
              </div>
            ))}
          </div>
        ) : listItems.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-3 text-gray-500">This list is empty.</p>
            <p className="text-sm text-gray-400 mt-1">
              Add resources from search results or resource pages.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {listItems.map((item: PersonalItem) => (
              <div
                key={item.resource_id}
                className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between hover:border-acad-300 transition-colors"
              >
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/resource/${item.resource_id}`)}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-acad-50 px-2 py-0.5 text-xs font-medium text-acad-600 capitalize">
                      {item.resource_type}
                    </span>
                    <h3 className="text-sm font-medium text-gray-900 hover:text-acad-600">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Added {new Date(item.added_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() =>
                    removeItemMutation.mutate({
                      listId: selectedListId,
                      resourceId: item.resource_id,
                    })
                  }
                  className="ml-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove from list"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Main lists view
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Lists</h1>
          <p className="text-gray-500 mt-1">Organize and save resources into personal lists</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create New List
        </button>
      </div>

      {listsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-5 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-2/3 mb-3" />
              <div className="h-3 bg-gray-200 rounded w-full mb-2" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center py-20">
          <div className="mx-auto w-20 h-20 rounded-full bg-acad-50 flex items-center justify-center mb-4">
            <List className="h-10 w-10 text-acad-300" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No lists yet</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            Create your first list to save resources. Organize journals, videos, and other materials
            into collections for easy access.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Your First List
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lists.map((list: PersonalList) => (
            <div
              key={list.list_id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:border-acad-300 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => setSelectedListId(list.list_id)}
                >
                  <h3 className="font-semibold text-gray-900 group-hover:text-acad-600">
                    {list.name}
                  </h3>
                  {list.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{list.description}</p>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteConfirm(list.list_id)
                  }}
                  className="ml-2 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                  title="Delete list"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div
                className="mt-3 flex items-center gap-3 text-xs text-gray-400 cursor-pointer"
                onClick={() => setSelectedListId(list.list_id)}
              >
                <span>{list.item_count} item{list.item_count !== 1 ? 's' : ''}</span>
                <span>&middot;</span>
                <span>{new Date(list.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Create New List</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setNewListName('')
                  setNewListDescription('')
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., Research Methods Papers"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-acad-500 focus:ring-1 focus:ring-acad-500 outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                  placeholder="Optional description for this list"
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-acad-500 focus:ring-1 focus:ring-acad-500 outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setNewListName('')
                  setNewListDescription('')
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newListName.trim() || createMutation.isPending}
                className="rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createMutation.isPending ? 'Creating...' : 'Create List'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete List</h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone. All items in this list will be removed.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
